import {
    buildSceneInstanceRecords,
    getSceneInstancePath,
} from './sceneInstanceIdentity'

export const CART_SCENE_OBJECT_SNAPSHOT_VERSION = 1

const ACCESSORY_PREFIX = 'monument-accessory::'

const asObject = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
)

const cloneValue = (value, fallback = null) => {
    try {
        return JSON.parse(JSON.stringify(value))
    } catch (_error) {
        return fallback
    }
}

const normalizeId = (value) => String(value || '').trim()

const getParentScenePath = (object = {}) => {
    const binding = asObject(
        object?.surfaceBinding ||
        object?.textData?.surfaceBinding ||
        object?.imageData?.surfaceBinding
    )

    return normalizeId(
        binding?.parentModelId ||
        binding?.parentSceneObjectId ||
        object?.parentObjectId ||
        object?.parentModelId ||
        object?.parentSceneObjectId
    )
}

const getRecordByPath = (scene = {}, scenePath = '') => {
    const path = normalizeId(scenePath)
    if (!path) return null

    return buildSceneInstanceRecords(
        scene?.selectedModels || {},
        scene?.instanceIds || {},
        scene?.copyPaths || {}
    ).find((record) => normalizeId(record?.path) === path) || null
}

const collectReferencedAssetKeys = (value, assets = {}, out = new Set(), depth = 0) => {
    if (depth > 16 || value === null || value === undefined) return out

    if (typeof value === 'string') {
        if (Object.prototype.hasOwnProperty.call(assets, value)) out.add(value)
        return out
    }

    if (Array.isArray(value)) {
        value.forEach((item) => collectReferencedAssetKeys(item, assets, out, depth + 1))
        return out
    }

    if (typeof value !== 'object') return out

    Object.entries(value).forEach(([key, item]) => {
        if (Object.prototype.hasOwnProperty.call(assets, key)) out.add(key)
        collectReferencedAssetKeys(item, assets, out, depth + 1)
    })

    return out
}

export const buildCartSceneObjectSnapshot = ({
    scene = {},
    scenePath = '',
    productId = '',
    categoryKey = '',
    sceneInstanceId = '',
    sceneCopyPath = '',
    sourceSceneId = '',
} = {}) => {
    const path = normalizeId(scenePath)
    if (!path) return null

    const record = getRecordByPath(scene, path)
    const resolvedProductId = normalizeId(productId || record?.productId)
    const resolvedCategoryKey = normalizeId(categoryKey || record?.categoryKey)
    const resolvedInstanceId = normalizeId(sceneInstanceId || record?.instanceId)
    const resolvedCopyPath = String(
        sceneCopyPath !== undefined && sceneCopyPath !== null
            ? sceneCopyPath
            : (record?.copyPath || '')
    )
    const categoryColors = Array.isArray(scene?.colors?.[resolvedCategoryKey])
        ? scene.colors[resolvedCategoryKey]
        : []
    const colorValue = scene?.colors?.instanceByPath?.[path]
        ?? categoryColors?.[record?.index]
        ?? categoryColors?.[0]
        ?? null
    const ownedCustomObjects = {}
    const ownedCustomIds = []

    Object.entries(scene?.customObjects || {}).forEach(([id, object]) => {
        if (getParentScenePath(object) !== path) return
        ownedCustomObjects[id] = cloneValue(object, object)
        ownedCustomIds.push(id)
    })

    const customTransforms = {
        positions: {},
        rotations: {},
        scales: {},
        pivotPoints: {},
        pivotPresetById: {},
        dimensions: {},
        hiddenModels: {},
        solidObjects: {},
        modelPartsMeta: {},
    }

    ownedCustomIds.forEach((id) => {
        Object.keys(customTransforms).forEach((field) => {
            if (Object.prototype.hasOwnProperty.call(scene?.[field] || {}, id)) {
                customTransforms[field][id] = cloneValue(scene[field][id], scene[field][id])
            }
        })
    })

    const assembly = scene?.monumentAssemblies?.[path] || null
    const decorations = scene?.monumentDecorations?.[path] || null
    const allAssets = asObject(scene?.monumentDecorationAssets)
    const referencedAssetKeys = collectReferencedAssetKeys(
        { decorations, customObjects: ownedCustomObjects },
        allAssets
    )
    const decorationAssets = {}

    referencedAssetKeys.forEach((key) => {
        decorationAssets[key] = cloneValue(allAssets[key], allAssets[key])
    })

    return {
        version: CART_SCENE_OBJECT_SNAPSHOT_VERSION,
        sourceSceneId: normalizeId(sourceSceneId),
        productId: resolvedProductId,
        categoryKey: resolvedCategoryKey,
        sceneInstanceId: resolvedInstanceId,
        sceneCopyPath: resolvedCopyPath,
        scenePath: path,
        transform: {
            position: cloneValue(scene?.positions?.[path], null),
            rotation: cloneValue(scene?.rotations?.[path], null),
            scale: cloneValue(scene?.scales?.[path], null),
            pivotPoint: cloneValue(scene?.pivotPoints?.[path], null),
            pivotPreset: cloneValue(scene?.pivotPresetById?.[path], null),
        },
        dimensions: cloneValue(scene?.dimensions?.[path], null),
        hidden: scene?.hiddenModels?.[path] === true,
        solid: scene?.solidObjects?.[path] === true,
        color: cloneValue(colorValue, colorValue),
        assembly: cloneValue(assembly, null),
        decorations: cloneValue(decorations, null),
        decorationAssets,
        customObjects: ownedCustomObjects,
        customTransforms,
        modelPartsMeta: cloneValue(scene?.modelPartsMeta?.[path], null),
    }
}

export const getCartSceneObjectSnapshot = (item = {}) => {
    const calc = asObject(item?.calculatorData)
    const direct = asObject(calc?.sceneObjectSnapshot)

    if (direct?.version && direct?.scenePath) {
        return cloneValue(direct, direct)
    }

    const legacyScene = asObject(calc?.sceneState)
    const requestedPath = normalizeId(
        calc?.modelId ||
        calc?.sceneModelId ||
        direct?.scenePath
    )

    if (!requestedPath) return null

    const sourceRecord = getRecordByPath(legacyScene, requestedPath)
    const snapshot = buildCartSceneObjectSnapshot({
        scene: legacyScene,
        scenePath: requestedPath,
        productId: calc?.productId || sourceRecord?.productId || item?.productId || item?.product || item?._id,
        categoryKey: calc?.categoryKey || sourceRecord?.categoryKey || item?.category,
        sceneInstanceId: calc?.sceneInstanceId || sourceRecord?.instanceId,
        sceneCopyPath: calc?.sceneCopyPath ?? sourceRecord?.copyPath ?? '',
        sourceSceneId: calc?.sourceSceneId || legacyScene?.sceneId || '',
    })

    if (!snapshot) return null

    if (!snapshot.assembly && calc?.assembly) snapshot.assembly = cloneValue(calc.assembly, null)
    if (!snapshot.decorations && Array.isArray(calc?.sceneDecorations) && calc.sceneDecorations.length) {
        snapshot.decorations = { decorations: cloneValue(calc.sceneDecorations, []) }
    }

    return snapshot
}

const REFERENCE_KEYS = new Set([
    'modelId',
    'sceneModelId',
    'parentModelId',
    'parentSceneObjectId',
    'parentObjectId',
    'ownerModelId',
    'ownerSceneObjectId',
    'targetModelId',
    'sourceModelId',
    'sceneObjectId',
])

const remapAccessoryId = (value, pathMap) => {
    if (typeof value !== 'string' || !value.startsWith(ACCESSORY_PREFIX)) return value

    const payload = value.slice(ACCESSORY_PREFIX.length)
    const parts = payload.split('::')
    if (parts.length < 3) return value

    let parent = ''
    try {
        parent = decodeURIComponent(parts[0] || '')
    } catch (_error) {
        parent = parts[0] || ''
    }

    const mappedParent = pathMap[parent]
    if (!mappedParent) return value

    return `${ACCESSORY_PREFIX}${encodeURIComponent(mappedParent)}::${parts.slice(1).join('::')}`
}

export const remapSceneObjectReferences = (value, pathMap = {}, depth = 0, parentKey = '') => {
    if (depth > 18 || value === null || value === undefined) return value

    if (typeof value === 'string') {
        const accessory = remapAccessoryId(value, pathMap)
        if (accessory !== value) return accessory
        if (REFERENCE_KEYS.has(parentKey) && pathMap[value]) return pathMap[value]
        return value
    }

    if (Array.isArray(value)) {
        return value.map((item) => remapSceneObjectReferences(item, pathMap, depth + 1, parentKey))
    }

    if (typeof value !== 'object') return value

    return Object.entries(value).reduce((result, [key, item]) => {
        const nextKey = pathMap[key] || key
        result[nextKey] = remapSceneObjectReferences(item, pathMap, depth + 1, key)
        return result
    }, {})
}

const makeCustomRestoreId = (sourceId, targetPath, occupied) => {
    const safeSource = String(sourceId || 'decoration').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 56)
    const safeTarget = String(targetPath || 'scene').replace(/[^a-zA-Z0-9_-]/g, '_').slice(-24)
    let candidate = `${safeSource}__restore_${safeTarget}`
    let index = 1

    while (occupied.has(candidate)) {
        candidate = `${safeSource}__restore_${safeTarget}_${index}`
        index += 1
    }

    occupied.add(candidate)
    return candidate
}

export const prepareCartSceneObjectRestore = (
    snapshot = {},
    {
        targetPath = '',
        existingCustomObjects = {},
    } = {}
) => {
    const sourcePath = normalizeId(snapshot?.scenePath)
    const target = normalizeId(targetPath)
    const pathMap = {}

    if (sourcePath && target && sourcePath !== target) pathMap[sourcePath] = target

    const occupied = new Set(Object.keys(existingCustomObjects || {}))
    const customIdMap = {}

    Object.keys(snapshot?.customObjects || {}).forEach((sourceId) => {
        if (!occupied.has(sourceId)) {
            occupied.add(sourceId)
            return
        }

        customIdMap[sourceId] = makeCustomRestoreId(sourceId, target, occupied)
    })

    Object.assign(pathMap, customIdMap)

    const remappedCustomObjects = {}
    Object.entries(snapshot?.customObjects || {}).forEach(([sourceId, object]) => {
        const targetId = customIdMap[sourceId] || sourceId
        remappedCustomObjects[targetId] = remapSceneObjectReferences(
            cloneValue(object, object),
            pathMap
        )

        if (remappedCustomObjects[targetId] && typeof remappedCustomObjects[targetId] === 'object') {
            remappedCustomObjects[targetId].id = targetId
        }
    })

    const remapCustomMap = (source = {}) => Object.entries(source || {}).reduce((result, [sourceId, value]) => {
        const targetId = customIdMap[sourceId] || sourceId
        result[targetId] = remapSceneObjectReferences(cloneValue(value, value), pathMap)
        return result
    }, {})

    const customTransforms = asObject(snapshot?.customTransforms)

    return {
        sourcePath,
        targetPath: target,
        pathMap,
        customIdMap,
        assembly: remapSceneObjectReferences(cloneValue(snapshot?.assembly, null), pathMap),
        decorations: remapSceneObjectReferences(cloneValue(snapshot?.decorations, null), pathMap),
        decorationAssets: remapSceneObjectReferences(cloneValue(snapshot?.decorationAssets, {}), pathMap),
        modelPartsMeta: remapSceneObjectReferences(cloneValue(snapshot?.modelPartsMeta, null), pathMap),
        customObjects: remappedCustomObjects,
        customTransforms: {
            positions: remapCustomMap(customTransforms?.positions),
            rotations: remapCustomMap(customTransforms?.rotations),
            scales: remapCustomMap(customTransforms?.scales),
            pivotPoints: remapCustomMap(customTransforms?.pivotPoints),
            pivotPresetById: remapCustomMap(customTransforms?.pivotPresetById),
            dimensions: remapCustomMap(customTransforms?.dimensions),
            hiddenModels: remapCustomMap(customTransforms?.hiddenModels),
            solidObjects: remapCustomMap(customTransforms?.solidObjects),
            modelPartsMeta: remapCustomMap(customTransforms?.modelPartsMeta),
        },
    }
}

export const getSnapshotScenePath = (snapshot = {}) => normalizeId(snapshot?.scenePath)
export const getSnapshotInstancePath = (snapshot = {}) => getSceneInstancePath(
    snapshot?.productId,
    snapshot?.sceneCopyPath || ''
)
